import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components/LoadingSpinner';
import { icon } from '../shared/components/icons';
// R83 #4: STATIC import — TemplatesPage was the only Admin tab living in its
// own network-fetched chunk (its four siblings are all statically imported by
// App.ts), so a stale shell after a deploy 404'd its chunk and the tab hung
// on "Loading…" forever. Bundled with AdminPage it can never miss.
import { TemplatesPage } from './TemplatesPage';

// Admin — one home for every backend setting that shapes a number elsewhere
// (docs/redesign/05-admin.md). Replaces four nav items: Owners & Properties,
// Configuration, Tasks & Expenses, Booking Quote.
//
// Tab 1 (Properties) is the new artifact: one registry row per listing with
// owner / type / comp tier / formula / goal / MTR all legible and editable
// in place. Tabs 2-4 mount the existing pages whole — Admin never
// re-implements what already works.
export class AdminPage {
  private container: HTMLElement;
  private tab: 'properties' | 'owners' | 'config' | 'expenses' | 'templates' | 'ops' = 'properties';
  private listings: any[] = [];
  private owners: any[] = [];
  private assignments: Record<string, string> = {};
  private types: Record<string, string> = {};
  private tiers: Record<string, number> = {};
  private formulas: any[] = [];
  private formulaByListing: Record<string, string> = {};
  private mtr: Record<string, any> = {};
  private goals: any = null;
  private search = '';
  private exception = '';   // '', 'no_owner', 'default_fee', 'no_projection', 'mtr'
  private page = 0;
  private static PAGE = 50;

  constructor(container: HTMLElement) { this.container = container; }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">
        <style>
          .ad-tabs { display:flex; gap:8px; margin-bottom:18px; }
          .ad-tab { padding:7px 16px; border-radius:var(--radius-pill); font-size:13px; cursor:pointer; border:1px solid var(--border); background:var(--card); color:var(--text-muted); transition:background .12s ease, color .12s ease, border-color .12s ease; }
          .ad-tab:hover { background:var(--bg-subtle); color:var(--text); }
          .ad-tab.active { font-weight:600; border-color:var(--text); background:var(--text); color:#fff; }
          .ad-card { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); margin-bottom:18px; }
          .ad-eyebrow { font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; }
          .ad-chip { font-size:11px; padding:4px 12px; border-radius:var(--radius-pill); cursor:pointer; border:1px solid var(--border); background:var(--card); color:var(--text-muted); transition:background .12s ease, color .12s ease, border-color .12s ease; }
          .ad-chip:hover { background:var(--bg-subtle); color:var(--text); }
          .ad-chip.active { border-color:var(--text); background:var(--text); color:#fff; }
          .ad-tbl { width:100%; border-collapse:collapse; font-size:12.5px; font-variant-numeric:tabular-nums; }
          .ad-tbl th { padding:10px 12px; text-align:left; font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; border-bottom:1px solid var(--border); white-space:nowrap; }
          .ad-tbl td { padding:9px 12px; border-bottom:1px solid var(--border-soft); white-space:nowrap; }
          .ad-tbl tbody tr { transition:background .1s ease; }
          .ad-tbl tbody tr:hover { background:var(--bg-subtle); }
          .ad-tbl tbody tr:last-child td { border-bottom:none; }
          .ad-tbl select { font-size:11.5px; padding:4px 8px; border:1px solid var(--border); border-radius:6px; background:var(--card); color:var(--text); max-width:130px; }
          .ad-name { max-width:220px; overflow:hidden; text-overflow:ellipsis; cursor:pointer; font-weight:600; color:var(--text); }
          .ad-name:hover { color:var(--info); }
          /* Mounted child pages bring their own intro/padding — strip both. */
          #ad-host .page-content { padding:0; }
          #ad-host .page-intro { display:none; }
        </style>
        <div class="dash-head">
          <h1>Admin</h1>
          <p>Every backend setting that shapes a number elsewhere — owners, formulas, comp tiers, goals and internal expenses, in one place.</p>
        </div>
        <div class="ad-tabs" id="ad-tabs">
          <button class="ad-tab active" data-ad-tab="properties">Properties</button>
          <button class="ad-tab" data-ad-tab="owners">Owners</button>
          <button class="ad-tab" data-ad-tab="config">Formulas & system</button>
          <button class="ad-tab" data-ad-tab="expenses">Internal expenses</button>
          <button class="ad-tab" data-ad-tab="templates">Templates</button>
          <button class="ad-tab" data-ad-tab="ops">Ops data</button>
        </div>
        <div id="ad-host"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
      </div>`;
    document.getElementById('ad-tabs')?.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-ad-tab]');
      if (!btn) return;
      this.tab = btn.dataset.adTab as any;
      document.querySelectorAll('.ad-tab').forEach(b => b.classList.toggle('active', (b as HTMLElement).dataset.adTab === this.tab));
      this.mountTab();
    });
    await this.mountTab();
  }

  private child: any = null;   // review R100 #8: the mounted tab page, so its draft veto reaches App.autoRefresh
  suspendAutoRefresh(): boolean { return !!(this.child && typeof this.child.suspendAutoRefresh === 'function' && this.child.suspendAutoRefresh()); }

  private mountSeq = 0;   // review #19: fast tab clicks race un-awaited mounts — only the newest may paint
  private async mountTab(): Promise<void> {
    const seq = ++this.mountSeq;
    const host = document.getElementById('ad-host');
    if (!host) return;
    host.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    this.child = null;
    // R83 #4 root cause of the eternal "Loading…": a rejected dynamic import
    // (a stale tab requesting a chunk hash the server no longer has) left the
    // spinner up forever — mountTab had no catch and the click path didn't
    // await it. Any failure now lands an error card with a Retry.
    try {
      if (this.tab === 'properties') {
        // ONE implementation: the Property Settings page (Portfolio hub) is the
        // same registry — mounted here too so Admin keeps a door to it (V8 #1).
        const { PropertySettingsPage } = await import('./PropertySettingsPage');
        await new PropertySettingsPage(host).render();
      } else if (this.tab === 'owners') {
        const { OwnersPage } = await import('./OwnersPage');
        await new OwnersPage(host).render();
      } else if (this.tab === 'config') {
        // R118 review #16: hand the page to the auto-refresh veto (this.child) —
        // R118's gross section renders here too (open modal, unsaved channel
        // rates, a dry run waiting for Apply), and the 15-min refresh re-mounted
        // a fresh SettingsPage over it. Only the newest mount may claim child.
        const { SettingsPage } = await import('./SettingsPage');
        const sp = new SettingsPage(host);
        if (seq === this.mountSeq) this.child = sp;
        await sp.render();
      } else if (this.tab === 'expenses') {
        // R100 A2: ONE implementation — the sheet now lives in its own page so
        // the Accounting hub can mount it too; Admin keeps its door.
        const { InternalExpensesPage } = await import('./InternalExpensesPage');
        const ie = new InternalExpensesPage(host); this.child = ie;
        await ie.render();
      } else if (this.tab === 'templates') {
        // R79 #2: one home for the global checklists/templates.
        const tp = new TemplatesPage(host); this.child = tp;
        await tp.render();
      } else {
        const { OperationsPage } = await import('./OperationsPage');
        await new OperationsPage(host).render();
      }
    } catch (e: any) {
      if (seq !== this.mountSeq) return;   // a newer tab already painted — don't cover it with a stale error card
      host.innerHTML = `
        <div style="padding:40px; text-align:center;">
          <div style="font-size:13px; color:var(--danger-text,#991b1b); margin-bottom:10px;">This tab failed to load — usually a fresh deploy behind a stale browser tab.</div>
          <div style="font-size:11.5px; color:var(--text-muted); margin-bottom:12px;">${(e && e.message) ? String(e.message).slice(0, 200) : 'unknown error'}</div>
          <button id="ad-tab-retry" class="btn btn-secondary">↻ Retry</button>
          <button id="ad-tab-reload" class="btn btn-secondary" style="margin-left:6px;">Reload the app</button>
        </div>`;
      document.getElementById('ad-tab-retry')?.addEventListener('click', () => { void this.mountTab(); });
      document.getElementById('ad-tab-reload')?.addEventListener('click', () => window.location.reload());
    }
  }

  // ── Tab 1: the registry ─────────────────────────────────────
  private async loadRegistry(): Promise<void> {
    const get = (u: string) => fetch(u).then(r => r.ok ? r.json() : null).catch(() => null);
    const [listings, owners, assigns, types, tiers, fAssigns, formulas, mtr, goals] = await Promise.all([
      get('/api/listings'),
      get('/api/owners'),
      get('/api/owners/assignments'),
      get('/api/listings/property-types'),
      get('/api/listings/comp-percentiles'),
      get('/api/formula-assignments'),
      get('/api/formulas'),
      get('/api/mtr-tags'),
      get('/api/goals/summary'),
    ]);
    this.listings = (Array.isArray(listings) ? listings : [])
      .filter((l: any) => (l.status || 'active') === 'active')
      .sort((a: any, b: any) => String(a.short_name || a.name || '').localeCompare(String(b.short_name || b.name || '')));
    this.owners = Array.isArray(owners) ? owners : (owners?.owners || []);
    this.assignments = assigns?.assignments || assigns || {};
    this.types = types?.types || {};
    this.tiers = tiers?.percentiles || tiers || {};
    this.formulas = Array.isArray(formulas) ? formulas : [];
    this.formulaByListing = {};
    const fa = Array.isArray(fAssigns) ? fAssigns : (fAssigns?.assignments || []);
    for (const a of fa) {
      const open = a.assignment || a; // rows nest the live assignment
      if (open && !open.effective_to && open.formula_code) {
        this.formulaByListing[String(a.pms_listing_id || a.listing_id)] = open.formula_code;
      }
    }
    this.mtr = mtr || {};
    this.goals = goals;
  }

  private money(v: any): string { const n = Number(v); return Number.isFinite(n) ? '$' + Math.round(n).toLocaleString() : '—'; }

  // SHARED MTR-membership contract (client mirror of server.js / statements):
  // 'never' always wins; else type==='mtr' OR forced 'always'/true OR min_nights>=28.
  private isMtr(l: any): boolean {
    const id = String(l.pms_listing_id);
    const tag = (this.mtr as any)[id];
    if (tag === 'never') return false;
    return this.types[id] === 'mtr' || tag === 'always' || tag === true || (Number(l.min_nights) || 0) >= 28;
  }

  private drawRegistry(): void {
    const host = document.getElementById('ad-host');
    if (!host) return;
    const goalsById = new Map((this.goals?.items || []).map((g: any) => [String(g.id), g]));
    const pid = (l: any) => String(l.pms_listing_id);

    // Stat line + exceptions computed over the active roster.
    const noOwner = this.listings.filter(l => !this.assignments[pid(l)]);
    const defaultFee = this.listings.filter(l => !this.formulaByListing[pid(l)]);
    const noProj = this.listings.filter(l => !goalsById.has(pid(l)) || !(goalsById.get(pid(l)) as any)?.goal);
    const mtrTagged = this.listings.filter(l => this.isMtr(l));
    const statline = document.getElementById('ad-statline');
    if (statline) statline.textContent =
      `${this.listings.length} properties · ${this.owners.length} owners · ${this.formulas.length} formulas`;

    const totalGoal = this.goals?.portfolio?.goal;
    const projected = this.listings.length - noProj.length;

    const exceptions: [string, string, number][] = [
      ['no_owner', 'no owner', noOwner.length],
      ['default_fee', 'on default fee', defaultFee.length],
      ['no_projection', 'no projection', noProj.length],
      ['mtr', 'MTR', mtrTagged.length],
    ];

    let rows = this.listings.filter(l =>
      !this.search || String(l.short_name || l.name || '').toLowerCase().includes(this.search));
    if (this.exception === 'no_owner') rows = rows.filter(l => !this.assignments[pid(l)]);
    if (this.exception === 'default_fee') rows = rows.filter(l => !this.formulaByListing[pid(l)]);
    if (this.exception === 'no_projection') rows = rows.filter(l => !goalsById.has(pid(l)) || !(goalsById.get(pid(l)) as any)?.goal);
    if (this.exception === 'mtr') rows = rows.filter(l => this.isMtr(l));

    const pages = Math.max(1, Math.ceil(rows.length / AdminPage.PAGE));
    if (this.page >= pages) this.page = 0;
    const pageRows = rows.slice(this.page * AdminPage.PAGE, (this.page + 1) * AdminPage.PAGE);

    const ownerOpts = (sel: string) => `<option value="">—</option>` + this.owners.map((o: any) =>
      `<option value="${Formatters.escapeHtml(String(o.id))}" ${String(o.id) === sel ? 'selected' : ''}>${Formatters.escapeHtml(o.name || 'Owner')}</option>`).join('');
    const typeOpts = (sel: string) => ['standard', 'mtr', 'owner-occupied', 'inactive'].map(t =>
      `<option value="${t}" ${t === sel ? 'selected' : ''}>${t}</option>`).join('');
    const tierOpts = (sel: number) => [25, 50, 75, 90].map(p =>
      `<option value="${p}" ${p === sel ? 'selected' : ''}>p${p}</option>`).join('');
    const formulaOpts = (sel: string) => `<option value="">default</option>` + this.formulas.map((f: any) =>
      `<option value="${Formatters.escapeHtml(f.formula_code)}" ${f.formula_code === sel ? 'selected' : ''}>${Formatters.escapeHtml(f.formula_code)}</option>`).join('');

    host.innerHTML = `
      <div class="kpi-grid cols-3">
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">${new Date().getFullYear()} auto-projected goal</span><span class="kpi-card-ic">${icon('target', 17)}</span></div>
          <div class="kpi-card-value">${this.money(totalGoal)}</div>
          <div class="kpi-card-cap">at assigned comp tiers (p50 default)</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Properties projected</span><span class="kpi-card-ic">${icon('building', 17)}</span></div>
          <div class="kpi-card-value">${projected} / ${this.listings.length}</div>
          <div class="kpi-card-cap"><a data-ad-exc="no_projection" style="color:var(--info); cursor:pointer;">${noProj.length} missing</a></div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Projections</span><span class="kpi-card-ic">${icon('sparkles', 17)}</span></div>
          <div style="display:flex; gap:8px; margin-top:14px;">
            <button class="btn btn-secondary btn-sm" id="ad-refresh-proj">Refresh projections</button>
            <button class="btn btn-secondary btn-sm" id="ad-view-pacing">View pacing →</button>
          </div>
        </div>
      </div>

      <div style="display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin:18px 0 10px;">
        ${exceptions.map(([k, label, n]) =>
          `<button class="ad-chip${this.exception === k ? ' active' : ''}" data-ad-exc="${k}">${n} ${label}</button>`).join('')}
        <input id="ad-search" type="search" placeholder="Search…" value="${Formatters.escapeHtml(this.search)}"
               style="margin-left:auto; font-size:12px; padding:5px 10px; border:1px solid var(--border); border-radius:6px; width:180px;">
      </div>

      <div class="pq-card">
        <div class="u-table-wrap">
        <table class="ad-tbl">
          <thead><tr><th>Property</th><th>Id</th><th>Owner</th><th>Type</th><th>Comp tier</th><th>Formula</th><th style="text-align:right;">Annual goal</th><th>MTR</th><th>⋯</th></tr></thead>
          <tbody>
            ${pageRows.map(l => {
              const id = pid(l);
              const g: any = goalsById.get(id);
              const fee = this.formulas.find((f: any) => f.formula_code === this.formulaByListing[id]);
              // MTR override: mtr_tags[id] is 'always'|'never'|true(→always); else 'auto'.
              const tag = (this.mtr as any)[id];
              const mtrMode = tag === 'never' ? 'never' : (tag === 'always' || tag === true) ? 'always' : 'auto';
              const autoResolves = this.types[id] === 'mtr' || (Number(l.min_nights) || 0) >= 28 ? 'mtr' : 'standard';
              return `<tr>
                <td class="ad-name" data-ad-open="${id}" title="${Formatters.escapeHtml(l.short_name || l.name || '')}">${Formatters.escapeHtml(l.short_name || l.name || id)}</td>
                <td class="mono num" style="color:var(--text-muted);">${id}</td>
                <td><select data-ad-owner="${id}">${ownerOpts(this.assignments[id] || '')}</select></td>
                <td><select data-ad-type="${id}">${typeOpts(this.types[id] || 'standard')}</select></td>
                <td><select data-ad-tier="${id}">${tierOpts(this.tiers[id] || 50)}</select></td>
                <td><select data-ad-formula="${id}">${formulaOpts(this.formulaByListing[id] || '')}</select>
                    <span style="font-size:10px; color:var(--text-muted);">${fee ? (Number(fee.commission_rate || 0) * 100).toFixed(1) + '%' : ''}</span></td>
                <td class="mono num" style="text-align:right;">${g?.goal ? this.money(g.goal) : '—'}</td>
                <td><select data-ad-mtr-mode="${id}" style="max-width:90px;">
                      <option value="auto" ${mtrMode === 'auto' ? 'selected' : ''}>auto</option>
                      <option value="always" ${mtrMode === 'always' ? 'selected' : ''}>always</option>
                      <option value="never" ${mtrMode === 'never' ? 'selected' : ''}>never</option>
                    </select>
                    <span class="ad-mtr-hint" data-ad-mtr-hint="${id}" style="font-size:10px; color:var(--text-muted); display:${mtrMode === 'auto' ? 'inline' : 'none'};">auto → ${autoResolves}</span></td>
                <td><a data-ad-quote="${id}" style="color:var(--info); cursor:pointer; font-size:11px;">Quote…</a></td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
        </div>
        ${pages > 1 ? `<div style="display:flex; gap:8px; justify-content:flex-end; padding:8px 14px; font-size:12px;">
          <button class="de-toggle-btn" id="ad-prev" ${this.page === 0 ? 'disabled' : ''}>‹</button>
          <span style="align-self:center; color:var(--text-muted);">${this.page + 1}/${pages}</span>
          <button class="de-toggle-btn" id="ad-next" ${this.page >= pages - 1 ? 'disabled' : ''}>›</button>
        </div>` : ''}
      </div>`;
    this.bindRegistry();
  }

  private async post(url: string, body: any): Promise<void> {
    await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => {});
  }

  private bindRegistry(): void {
    const host = document.getElementById('ad-host');
    if (!host) return;
    host.querySelectorAll<HTMLElement>('[data-ad-exc]').forEach(b =>
      b.addEventListener('click', () => {
        const k = b.dataset.adExc!;
        this.exception = this.exception === k ? '' : k;
        this.page = 0;
        this.drawRegistry();
      }));
    document.getElementById('ad-search')?.addEventListener('input', (e) => {
      this.search = (e.target as HTMLInputElement).value.toLowerCase();
      this.page = 0;
      this.drawRegistry();
      const inp = document.getElementById('ad-search') as HTMLInputElement | null;
      if (inp) { inp.focus(); inp.setSelectionRange(inp.value.length, inp.value.length); }
    });
    document.getElementById('ad-prev')?.addEventListener('click', () => { this.page--; this.drawRegistry(); });
    document.getElementById('ad-next')?.addEventListener('click', () => { this.page++; this.drawRegistry(); });
    document.getElementById('ad-refresh-proj')?.addEventListener('click', async (e) => {
      (e.target as HTMLElement).textContent = 'refreshing…';
      await this.post('/api/projections/generate-from-airroi', { year: new Date().getFullYear() });
      await this.loadRegistry();
      this.drawRegistry();
    });
    document.getElementById('ad-view-pacing')?.addEventListener('click', () => {
      try { sessionStorage.setItem('hub_tab', 'tracking'); } catch { /* private mode */ }
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'analytics' }));
    });

    host.querySelectorAll<HTMLSelectElement>('[data-ad-owner]').forEach(s =>
      s.addEventListener('change', () => {
        this.assignments[s.dataset.adOwner!] = s.value;
        this.post('/api/listings/owner', { listing_id: s.dataset.adOwner, owner_id: s.value || null });
      }));
    host.querySelectorAll<HTMLSelectElement>('[data-ad-type]').forEach(s =>
      s.addEventListener('change', () => {
        this.types[s.dataset.adType!] = s.value;
        this.post('/api/listings/property-type', { id: s.dataset.adType, type: s.value });
      }));
    host.querySelectorAll<HTMLSelectElement>('[data-ad-tier]').forEach(s =>
      s.addEventListener('change', () => {
        this.tiers[s.dataset.adTier!] = parseInt(s.value, 10);
        this.post('/api/listings/comp-percentile', { id: s.dataset.adTier, percentile: parseInt(s.value, 10) });
      }));
    host.querySelectorAll<HTMLSelectElement>('[data-ad-formula]').forEach(s =>
      s.addEventListener('change', () => {
        const id = s.dataset.adFormula!;
        this.formulaByListing[id] = s.value;
        if (s.value) this.post('/api/formula-assignments', { listing_id: id, formula_code: s.value, effective_from: new Date().toISOString().slice(0, 10) });
      }));
    host.querySelectorAll<HTMLSelectElement>('[data-ad-mtr-mode]').forEach(sel =>
      sel.addEventListener('change', async () => {
        const id = sel.dataset.adMtrMode!;
        const mode = sel.value; // auto | always | never
        if (mode === 'auto') delete (this.mtr as any)[id];
        else (this.mtr as any)[id] = mode;
        const hint = host.querySelector<HTMLElement>(`[data-ad-mtr-hint="${id}"]`);
        if (hint) hint.style.display = mode === 'auto' ? 'inline' : 'none';
        await this.post('/api/mtr-tags', { listing_id: id, mode });
      }));
    host.querySelectorAll<HTMLElement>('[data-ad-open]').forEach(el =>
      el.addEventListener('click', () => {
        sessionStorage.setItem('workup_preselect', el.dataset.adOpen!);
        sessionStorage.setItem('workup_tab', 'propensity');
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'data-explorer' }));
      }));
    host.querySelectorAll<HTMLElement>('[data-ad-quote]').forEach(el =>
      el.addEventListener('click', () => {
        sessionStorage.setItem('quote_preselect', el.dataset.adQuote!);
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'booking-quote' }));
      }));
  }
}
